import { useState } from 'react'
import { Download, Pencil, Plus, ShieldAlert, Upload } from 'lucide-react'
import {
  ConfirmDialog, CurrencyValue, EmptyState, ErrorState, FormDialog, ImportDialog, PageHeader,
  PermissionGate,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { bulkPaths } from '@/lib/bulk'
import { usePerDiemRates, useTravelMutations } from '../api'
import type { PerDiemRateView } from '../types'
import { DatePicker } from '@/components/ui/date-picker'

/**
 * The daily allowance per destination.
 *
 * Nothing is seeded and nothing has a default. These are your figures, agreed with your
 * finance function and often with a tax position behind them; a rate this system supplied
 * would be paid to real people on nobody's authority.
 */
export function PerDiemRatesPage() {
  useBreadcrumb()
  const rates = usePerDiemRates()
  const { saveRate, closeRate } = useTravelMutations()

  const [draft, setDraft] = useState<{
    id?: string
    destination: string; countryCode: string; effectiveFrom: string; dailyAmount: string
    currency: string; includesAccommodation: boolean; notes: string
  } | null>(null)
  const [closing, setClosing] = useState<PerDiemRateView | null>(null)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Per-diem rates"
        subtitle="What a day away is worth, per destination."
        actions={
          <PermissionGate permission="expense:configure">
            <Button
              type="button" variant="outline"
              onClick={() => {
                void downloadFile(
                  bulkPaths('/v1/payroll/masters/per-diem').export, 'per-diem-rates.xlsx')
              }}
            >
              <Download className="size-4" aria-hidden="true" />
              Export
            </Button>
            <Button type="button" variant="outline" onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden="true" />
              Import
            </Button>
            <Button
              type="button"
              onClick={() => {
                setError(null)
                setDraft({
                  destination: '', countryCode: '', effectiveFrom: '', dailyAmount: '',
                  currency: 'AED', includesAccommodation: false, notes: '',
                })
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              New rate
            </Button>
          </PermissionGate>
        }
      />

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what="per-diem rates"
        prefix="/v1/payroll/masters/per-diem"
        invalidateKey={['travel']}
      />

      <ConfirmDialog
        open={closing !== null}
        onOpenChange={(open) => { if (!open) setClosing(null) }}
        title={`Stop applying the ${closing?.destination ?? ''} rate?`}
        description="It is dated out from today rather than removed, so a trip already settled at it keeps resolving to the figure it was paid."
        confirmLabel="Close it off"
        loading={closeRate.isPending}
        error={error}
        onConfirm={() => {
          if (!closing) return
          setError(null)
          closeRate.mutate(closing.id, {
            onSuccess: () => setClosing(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That rate could not be closed off.'),
          })
        }}
      />

      <Card>
        {rates.isPending && <Skeleton className="m-4 h-24" />}
        {rates.isError && <ErrorState error={rates.error} onRetry={() => void rates.refetch()} />}
        {rates.isSuccess && rates.data.length === 0 && (
          <EmptyState
            title="No per-diem rate is configured"
            description="Travel can still be requested — the advance is then entered by hand and the request says why."
          />
        )}
        {rates.isSuccess && rates.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Destination</th>
                  <th className="p-3 text-start font-medium">Country</th>
                  <th className="p-3 text-start font-medium">Grade</th>
                  <th className="p-3 text-start font-medium">From</th>
                  <th className="p-3 text-end font-medium">Per day</th>
                  <th className="p-3 text-start font-medium">Until</th>
                  <th className="p-3 text-start font-medium">Accommodation</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {rates.data.map((rate) => (
                  <tr key={rate.id} className="border-b border-slate-100">
                    <td className="p-3">{rate.destination}</td>
                    <td className="p-3 text-slate-600">{rate.countryCode ?? '—'}</td>
                    <td className="p-3 text-slate-600">{rate.gradeName ?? 'Every grade'}</td>
                    <td className="p-3 text-slate-600">{rate.effectiveFrom}</td>
                    <td className="p-3 text-end font-medium">
                      <CurrencyValue value={rate.dailyAmount} currency={rate.currency} />
                    </td>
                    <td className="p-3 text-slate-600">
                      {rate.effectiveTo ?? <span className="text-slate-500">still applies</span>}
                    </td>
                    <td className="p-3 text-slate-600">
                      {rate.includesAccommodation ? 'Included' : 'Claimed separately'}
                    </td>
                    <td className="p-3 text-end">
                      <PermissionGate permission="expense:configure">
                        <Button
                          type="button" variant="ghost" size="sm"
                          aria-label={`Edit ${rate.destination}`}
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: rate.id,
                              destination: rate.destination,
                              countryCode: rate.countryCode ?? '',
                              effectiveFrom: rate.effectiveFrom,
                              dailyAmount: String(rate.dailyAmount),
                              currency: rate.currency,
                              includesAccommodation: rate.includesAccommodation,
                              notes: rate.notes ?? '',
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {rate.effectiveTo == null && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => { setError(null); setClosing(rate) }}
                          >
                            Close off
                          </Button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        No rate is supplied with the system and none has a default. Rates are effective-dated,
        so a trip taken in March keeps the figure that was in force in March.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title={draft?.id ? 'Edit per-diem rate' : 'New per-diem rate'}
        description="Effective-dated: a later rate for the same destination supersedes this one from its own start date."
        submitLabel="Save rate"
        loading={saveRate.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveRate.mutateAsync({
                id: draft.id,
                destination: draft.destination,
                countryCode: draft.countryCode || null,
                effectiveFrom: draft.effectiveFrom,
                dailyAmount: Number(draft.dailyAmount),
                currency: draft.currency,
                includesAccommodation: draft.includesAccommodation,
                notes: draft.notes || null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That rate could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="rate-destination">Destination</Label>
                <Input
                  id="rate-destination" value={draft.destination}
                  onChange={(event) => setDraft({ ...draft, destination: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="rate-country">Country code</Label>
                <Input
                  id="rate-country" maxLength={2} value={draft.countryCode}
                  onChange={(event) =>
                    setDraft({ ...draft, countryCode: event.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label htmlFor="rate-from">In force from</Label>
                <DatePicker
                  id="rate-from" value={draft.effectiveFrom}
                  onChange={(event) => setDraft({ ...draft, effectiveFrom: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="rate-amount">Per day</Label>
                <Input
                  id="rate-amount" type="number" value={draft.dailyAmount}
                  onChange={(event) => setDraft({ ...draft, dailyAmount: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="rate-currency">Currency</Label>
                <Input
                  id="rate-currency" maxLength={3} value={draft.currency}
                  onChange={(event) =>
                    setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label htmlFor="rate-notes">Notes</Label>
                <Input
                  id="rate-notes" value={draft.notes}
                  onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.includesAccommodation}
                onChange={(event) =>
                  setDraft({ ...draft, includesAccommodation: event.target.checked })}
              />
              This rate includes accommodation
            </label>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
